import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { fetchTraineesByManagerId, getBatchesByManager } from '../../../api_service';

const AddVideoModal = ({ managerId, onClose, onCreated }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    videoUrl: '',
    durationSeconds: '',
    deadline: '',
  });
  const [trainees, setTrainees] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchTraineesByManagerId(managerId);
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setTrainees(list);
      } catch {
        setTrainees([]);
      }
      try {
        const res = await getBatchesByManager(managerId);
        setBatches(Array.isArray(res?.data) ? res.data : []);
      } catch {
        setBatches([]);
      }
    };
    load();
  }, [managerId]);

  const toggleTrainee = (userid) => {
    setSelectedIds(prev =>
      prev.includes(userid) ? prev.filter(x => x !== userid) : [...prev, userid]
    );
  };

  const applyBatch = (batchId) => {
    setSelectedBatch(batchId);
    if (!batchId) return;
    const b = batches.find(x => String(x.batchId) === String(batchId));
    if (!b || !Array.isArray(b.members)) return;
    const ids = b.members.map(m => m.userid);
    setSelectedIds(prev => Array.from(new Set([...prev, ...ids])));
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!form.videoUrl.trim()) { setError('Video URL is required'); return; }
    if (!form.deadline) { setError('Deadline is required'); return; }
    if (selectedIds.length === 0) { setError('Select at least one trainee'); return; }
    setLoading(true);
    setError('');
    try {
      await onCreated({
        title: form.title,
        description: form.description,
        videoUrl: form.videoUrl,
        durationSeconds: form.durationSeconds === '' ? null : Number(form.durationSeconds),
        deadline: form.deadline,
        traineeIds: selectedIds,
      });
      onClose();
    } catch {
      setError('Failed to add video. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Add New Video</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none">&times;</button>
        </div>

        <div className="overflow-y-auto p-6 space-y-4 flex-1">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Video Title *</label>
            <Input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Introduction to Finacle Core Banking"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Description</label>
            <textarea
              className="w-full border border-border rounded-lg p-3 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              rows={3}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe what the video covers..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Video URL *</label>
            <Input
              value={form.videoUrl}
              onChange={e => setForm(f => ({ ...f, videoUrl: e.target.value }))}
              placeholder="YouTube, Vimeo or MP4 link..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Duration (seconds)</label>
              <Input
                type="number"
                value={form.durationSeconds}
                onChange={e => setForm(f => ({ ...f, durationSeconds: e.target.value }))}
                min={0}
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Deadline *</label>
              <Input
                type="date"
                value={form.deadline}
                onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))}
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="block text-sm font-medium text-foreground mb-1">Quick-add from batch</label>
            <select
              value={selectedBatch}
              onChange={e => applyBatch(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Select a batch to add its trainees…</option>
              {batches.map(b => (
                <option key={b.batchId} value={b.batchId}>
                  {b.name} ({Array.isArray(b.members) ? b.members.length : 0})
                </option>
              ))}
            </select>
            {batches.length === 0 && (
              <p className="text-xs text-muted-foreground mt-1">No batches yet — create one in Batches.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Assign Trainees * <span className="text-muted-foreground font-normal">({selectedIds.length} selected)</span>
            </label>
            <div className="border border-border rounded-lg divide-y divide-border max-h-48 overflow-y-auto">
              {trainees.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">No trainees found</p>
              ) : (
                trainees.map(t => (
                  <label key={t.userid} className="flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(t.userid)}
                      onChange={() => toggleTrainee(t.userid)}
                      className="accent-primary"
                    />
                    <span className="text-sm text-foreground">{t.firstname} {t.lastname}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{t.emailid}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          {error && <p className="text-sm text-error">{error}</p>}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Adding...' : 'Add Video'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddVideoModal;
